import React, { useState, useEffect } from 'react';
import './App.css';

// R4: a render crash must show a friendly reload screen, not a blank page
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Render error caught by boundary:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <p>Sorry — the page hit a problem. Please reload to carry on.</p>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const LOYALTY_STORAGE_KEY = 'loyalty_card';

// Wallet passes only open on Apple devices — hide the button elsewhere.
const IS_APPLE_DEVICE = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);

// The official-style "Add to Apple Wallet" badge: coloured wallet glyph +
// text on black, per Apple's add-to-wallet guidelines. Inline SVG so it's
// crisp everywhere with nothing to load.
function AddToWalletBadge() {
  return (
    <span className="wallet-badge-inner">
      <svg viewBox="0 0 44 36" className="wallet-badge-glyph" aria-hidden="true">
        <rect x="2" y="0" width="40" height="26" rx="4" fill="#469CD9" />
        <rect x="2" y="6" width="40" height="26" rx="4" fill="#F5B516" />
        <rect x="2" y="12" width="40" height="26" rx="4" fill="#58B947" />
        <rect x="2" y="18" width="40" height="26" rx="4" fill="#DE4B39" />
        <path d="M0 22 h44 v10 a4 4 0 0 1 -4 4 H4 a4 4 0 0 1 -4 -4 z" fill="#DEDBCE" />
      </svg>
      <span>Add to Apple Wallet</span>
    </span>
  );
}
const STAMPS_FOR_FREE_DRINK = 9;

function loadStoredLoyaltyCard() {
  try {
    const raw = localStorage.getItem(LOYALTY_STORAGE_KEY);
    if (!raw) return null;
    const card = JSON.parse(raw);
    if (card && typeof card.word1 === 'string' && typeof card.word2 === 'string') {
      return { word1: card.word1, word2: card.word2, stamps: Number(card.stamps) || 0 };
    }
  } catch (e) {
    console.error('Error reading loyalty card from storage:', e);
  }
  return null;
}

function App() {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [temperature, setTemperature] = useState('Hot');
  const [milk, setMilk] = useState('None');
  const [size, setSize] = useState('Medium');
  const [caffeine, setCaffeine] = useState('Regular');
  const [selectedSyrup, setSelectedSyrup] = useState('');
  const [selectedTopping, setSelectedTopping] = useState('');
  const [selectedTeaFlavor, setSelectedTeaFlavor] = useState('');
  const [pickupTime, setPickupTime] = useState('now');
  const [pickupTimes, setPickupTimes] = useState([]);
  const [shopOpen, setShopOpen] = useState(true);
  const [closedMessage, setClosedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [basket, setBasket] = useState([]);
  const [showBasket, setShowBasket] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [nameFieldTouched, setNameFieldTouched] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [preloadedPickupTime, setPreloadedPickupTime] = useState(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [pendingPaymentIntentId, setPendingPaymentIntentId] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [orderTicketPI, setOrderTicketPI] = useState(null); // payment intent for the Wallet order ticket
  const [notificationSetupFailed, setNotificationSetupFailed] = useState(false);

  // FWOFO Mode (Flat White Or Fuck Off) - Easter egg mode
  const [fwofoMode, setFwofoMode] = useState(false);

  // R5: Stripe.js failed to load (CDN blocked, ad blocker, network)
  const [stripeUnavailable, setStripeUnavailable] = useState(false);
  // R12: menu load failure gets a visible error + retry, not endless "Loading"
  const [menuError, setMenuError] = useState(false);

  // ── Loyalty ──
  // The saved card is ONLY the two public words + last-known stamps.
  // The private third word is NEVER stored client-side.
  const [loyaltyCard, setLoyaltyCard] = useState(loadStoredLoyaltyCard);
  const [showLoyalty, setShowLoyalty] = useState(false);
  const [justJoined, setJustJoined] = useState(null); // { word1, word2 } shown BIG after joining
  const [joinWord, setJoinWord] = useState('');
  const [joinWordConfirm, setJoinWordConfirm] = useState('');
  const [joinPublic1, setJoinPublic1] = useState('');
  const [joinPublic2, setJoinPublic2] = useState('');
  const [pickForMe, setPickForMe] = useState(false);
  const [linkWord1, setLinkWord1] = useState('');
  const [linkWord2, setLinkWord2] = useState('');
  const [loyaltyBusy, setLoyaltyBusy] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState(null);
  // Checkout: collect stamps on this order? (chip with an x to opt out)
  const [includeLoyalty, setIncludeLoyalty] = useState(true);
  // Redeem: free drink applied to this order (word3 held in memory only)
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemWord3, setRedeemWord3] = useState('');
  const [redeemActive, setRedeemActive] = useState(false);
  const [loyaltyCheckoutError, setLoyaltyCheckoutError] = useState(null);
  // Apple Wallet pass: private word entered fresh each time (never stored)
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletWord3, setWalletWord3] = useState('');
  // Receipt claim code (/c/<code>): { code, status, stamps? } — status is
  // 'checking' | 'open' | 'claimed' | 'expired' | 'not_found' | 'error'
  const [claimInfo, setClaimInfo] = useState(null);

  // /c/<code> from a printed receipt: check the code and open the loyalty
  // view in claim mode. Claiming always mints a NEW card (server rule).
  useEffect(() => {
    const match = window.location.pathname.match(/^\/c\/([A-Za-z0-9]{6,12})$/);
    if (!match) return;
    const code = match[1].toUpperCase();
    setClaimInfo({ code, status: 'checking' });
    setShowLoyalty(true);
    (async () => {
      try {
        const response = await fetch('/.netlify/functions/loyalty-claim', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code })
        });
        let data = null;
        try { data = await response.json(); } catch (err) { /* non-JSON */ }
        if (response.ok && data && data.success) {
          setClaimInfo({ code, status: data.status, stamps: data.stamps });
        } else {
          setClaimInfo({ code, status: (data && data.code) || 'error' });
        }
      } catch (error) {
        console.error('Error checking claim code:', error);
        setClaimInfo({ code, status: 'error' });
      }
    })();
  }, []);
  const [walletPrintSecret, setWalletPrintSecret] = useState(false);
  const [walletError, setWalletError] = useState(null);

  // Stripe will be initialized when we get the publishable key from the backend

  // Auto-load FWOFO mode: add flat white to basket and show checkout
  useEffect(() => {
    // Check for FWOFO mode in URL path
    if (window.location.pathname === '/fwofo' && products.length > 0 && basket.length === 0 && !fwofoMode) {
      setFwofoMode(true);

      // Find flat white and add to basket
      const flatWhite = products.find(p => p.name === 'Flat White');
      if (flatWhite) {
        // Create basket item with same structure as normal mode
        const itemPrice = flatWhite.prices?.Hot?.Medium || flatWhite.price;
        setBasket([{
          id: Date.now(),
          name: 'Flat White',
          variant: 'Hot, Medium',
          price: itemPrice,
          vatRate: 20, // Hot drinks are 20% VAT
          quantity: 1
        }]);

        // Always show basket in FWOFO mode first
        setShowBasket(true);

        // Check if name is saved and set it after a small delay
        // This ensures showBasket is already true when payment pre-load checks
        const savedName = localStorage.getItem('fwofoName');
        if (savedName) {
          setTimeout(() => {
            setCustomerName(savedName);
            setNameFieldTouched(true); // Trigger payment pre-load
          }, 100);
        }
      }
    }
  }, [products, basket.length, fwofoMode]);

  // Load from cache immediately, then fetch updates in background
  useEffect(() => {

    // Check for payment_intent parameter from successful payment
    const urlParams = new URLSearchParams(window.location.search);
    const paymentIntent = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');

    if (paymentIntent && redirectStatus === 'succeeded') {
      setOrderResult({
        success: true,
        message: 'Payment successful! We\'ll have your order ready shortly.'
      });
      // Clear basket after successful payment
      setBasket([]);
      setCustomerName('');
      setCustomerEmail('');
      setClientSecret(null);
      setElements(null);
      // Clean up URL
      window.history.replaceState({}, '', '/');

      // Refresh loyalty stamps once the webhook has had a moment to run
      const storedCard = loadStoredLoyaltyCard();
      if (storedCard) {
        setTimeout(() => {
          refreshLoyaltyCard(storedCard.word1, storedCard.word2);
        }, 4000);
      }

      // Offer push notifications for order ready alerts
      offerPushNotifications(paymentIntent);
    } else if (redirectStatus === 'failed') {
      setOrderResult({
        success: false,
        error: 'Payment failed',
        message: 'Your order was not placed. Please try again.'
      });
      // Clean up URL
      window.history.replaceState({}, '', '/');
    }

    // Load cached data instantly
    const cachedProducts = localStorage.getItem('products_cache');
    const cachedPickupTimes = localStorage.getItem('pickup_times_cache');
    const cachedShopOpen = localStorage.getItem('shop_open_cache');

    if (cachedProducts) {
      try {
        setProducts(JSON.parse(cachedProducts));
      } catch (e) {
        console.error('Error parsing cached products:', e);
      }
    }

    if (cachedPickupTimes) {
      try {
        const times = JSON.parse(cachedPickupTimes);
        setPickupTimes(times);
        // Don't set pickupTime from cache - always default to 'now' for safety
        // Users should explicitly select a delay each time
      } catch (e) {
        console.error('Error parsing cached pickup times:', e);
      }
    }

    if (cachedShopOpen !== null) {
      setShopOpen(cachedShopOpen === 'true');
    }

    // Check if user has accepted cookies
    const cookieConsent = localStorage.getItem('cookie_consent');
    if (!cookieConsent) {
      setShowCookieBanner(true);
    }

    // Fetch fresh data in background
    fetchProducts();
    fetchPickupTimes();

    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchProducts();
      fetchPickupTimes();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPickupTimes = async () => {
    try {
      const response = await fetch('/.netlify/functions/get-pickup-times');
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setShopOpen(data.isOpen);
        setClosedMessage(data.closedMessage || null);
        setPickupTimes(data.pickupTimes);
        // Only update pickupTime if user's selection is no longer valid
        if (data.pickupTimes.length > 0) {
          setPickupTime(currentTime => {
            const isStillValid = data.pickupTimes.some(t => t.value === currentTime);
            return isStillValid ? currentTime : data.pickupTimes[0].value;
          });
        }
        // Cache the results
        localStorage.setItem('pickup_times_cache', JSON.stringify(data.pickupTimes));
        localStorage.setItem('shop_open_cache', String(data.isOpen));
      }
    } catch (error) {
      console.error('Error fetching pickup times:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('/.netlify/functions/get-curated-products');
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setProducts(data.products);
        setMenuError(false);
        // Cache the results
        localStorage.setItem('products_cache', JSON.stringify(data.products));
      } else {
        throw new Error(data.error || 'Failed to load menu');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      // Only surface the error when there's nothing to show (cache miss)
      setProducts(current => {
        if (current.length === 0) setMenuError(true);
        return current;
      });
    }
  };

  // ── Loyalty helpers ──

  const saveLoyaltyCard = (card) => {
    setLoyaltyCard(card);
    try {
      localStorage.setItem(LOYALTY_STORAGE_KEY, JSON.stringify(card));
    } catch (e) {
      console.error('Error saving loyalty card:', e);
    }
  };

  const clearLoyaltyCard = () => {
    setLoyaltyCard(null);
    setJustJoined(null);
    setRedeemOpen(false);
    setRedeemActive(false);
    setRedeemWord3('');
    setWalletOpen(false);
    setWalletWord3('');
    setWalletError(null);
    try {
      localStorage.removeItem(LOYALTY_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing loyalty card:', e);
    }
  };

  // Add to Apple Wallet: a REAL form submission (not fetch) so Safari
  // navigates to the .pkpass response and offers the native add sheet.
  // The private word rides along for verification; it is never stored here.
  const submitWalletPassForm = (word1, word2, word3, printSecret) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/.netlify/functions/wallet-pass';
    form.target = '_blank';
    const add = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    add('word1', word1);
    add('word2', word2);
    add('word3', word3);
    if (printSecret) add('printSecret', 'true');
    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  // Order ticket: same real-form trick, different endpoint — possession of
  // the payment intent id is the auth.
  const submitOrderTicketForm = (paymentIntentId) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/.netlify/functions/wallet-order-pass';
    form.target = '_blank';
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'payment_intent';
    input.value = paymentIntentId;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  const handleWalletAdd = (e) => {
    e.preventDefault();
    const word = walletWord3.trim().normalize('NFC').toLowerCase();
    if (!word) {
      setWalletError('Enter your private word first');
      return;
    }
    setWalletError(null);
    submitWalletPassForm(loyaltyCard.word1, loyaltyCard.word2, word, walletPrintSecret);
    setWalletOpen(false);
    setWalletWord3('');
  };

  // Refresh the stamp balance for saved words. Clears the saved card if the
  // server says it doesn't exist.
  const refreshLoyaltyCard = async (word1, word2) => {
    try {
      const response = await fetch('/.netlify/functions/loyalty-web-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word1, word2 })
      });
      if (response.status === 404) {
        clearLoyaltyCard();
        return null;
      }
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        const card = { word1, word2, stamps: data.stamps };
        saveLoyaltyCard(card);
        return card;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing loyalty card:', error);
      return null;
    }
  };

  // Refresh the balance every time the loyalty view is opened
  useEffect(() => {
    if (showLoyalty && loyaltyCard) {
      refreshLoyaltyCard(loyaltyCard.word1, loyaltyCard.word2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoyalty]);

  const handleLoyaltyJoin = async (e) => {
    e.preventDefault();
    setLoyaltyError(null);
    const word = joinWord.trim().normalize('NFC').toLowerCase();
    if (!/^[a-z\u00df-\u00f6\u00f8-\u00ff]{2,24}$/.test(word)) {
      setLoyaltyError('Your private word must be a single word, letters only (accents welcome), 2-24 characters');
      return;
    }
    if (word !== joinWordConfirm.trim().normalize('NFC').toLowerCase()) {
      setLoyaltyError('The two words don\'t match — please type the same word twice');
      return;
    }
    const pub1 = joinPublic1.trim().normalize('NFC').toLowerCase();
    const pub2 = joinPublic2.trim().normalize('NFC').toLowerCase();
    if (!pickForMe) {
      if (!/^[a-z\u00df-\u00f6\u00f8-\u00ff]{2,16}$/.test(pub1) || !/^[a-z\u00df-\u00f6\u00f8-\u00ff]{2,16}$/.test(pub2)) {
        setLoyaltyError('Your two public words must each be a single word, letters only (accents welcome), 2-16 characters');
        return;
      }
      if (pub1 === pub2) {
        setLoyaltyError('Your two public words must be different');
        return;
      }
    }
    // Never let a join slip past a claim code that hasn't been verified yet:
    // falling through to plain join would mint a card WITHOUT the stamps and
    // the customer would believe they claimed.
    if (claimInfo && (claimInfo.status === 'checking' || claimInfo.status === 'error')) {
      setLoyaltyError(claimInfo.status === 'checking'
        ? 'Still checking your receipt code — try again in a second'
        : 'Couldn\'t verify your receipt code — check your connection and reopen the link');
      return;
    }
    setLoyaltyBusy(true);
    try {
      // A live receipt claim code routes joining through /loyalty-claim so
      // the new card starts with the stamps that sale earned.
      const claiming = claimInfo && claimInfo.status === 'open';
      const body = { word3: word };
      if (!pickForMe) { body.word1 = pub1; body.word2 = pub2; }
      if (claiming) { body.code = claimInfo.code; }
      const response = await fetch(
        claiming ? '/.netlify/functions/loyalty-claim' : '/.netlify/functions/loyalty-web-join',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      let data = null;
      try {
        data = await response.json();
      } catch (err) {
        // non-JSON error page
      }
      if (response.ok && data && data.success) {
        const stamps = (claiming && data.account.stamps) || 0;
        saveLoyaltyCard({ word1: data.account.word1, word2: data.account.word2, stamps });
        // word3 rides along IN MEMORY ONLY so the welcome screen can offer
        // one-tap Add to Wallet without asking them to retype the word they
        // entered seconds ago. Gone as soon as the screen closes.
        setJustJoined({ word1: data.account.word1, word2: data.account.word2, stamps, word3: word });
        if (claiming) {
          setClaimInfo(null);
          window.history.replaceState({}, '', '/');
        }
        setJoinWord('');
        setJoinWordConfirm('');
        setJoinPublic1('');
        setJoinPublic2('');
      } else if (claiming && data && (data.code === 'claimed' || data.code === 'expired' || data.code === 'not_found' || data.code === 'void')) {
        setClaimInfo({ ...claimInfo, status: data.code });
      } else if (response.status === 429) {
        setLoyaltyError('Too many attempts — please try again later');
      } else {
        setLoyaltyError((data && data.error) || 'Could not create your card — please try again');
      }
    } catch (error) {
      console.error('Error joining loyalty:', error);
      setLoyaltyError('Network error — please try again');
    }
    setLoyaltyBusy(false);
  };

  const handleLoyaltyLink = async (e) => {
    e.preventDefault();
    setLoyaltyError(null);
    const word1 = linkWord1.trim().normalize('NFC').toLowerCase();
    const word2 = linkWord2.trim().normalize('NFC').toLowerCase();
    if (!word1 || !word2) {
      setLoyaltyError('Please enter both words');
      return;
    }
    setLoyaltyBusy(true);
    try {
      const response = await fetch('/.netlify/functions/loyalty-web-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word1, word2 })
      });
      let data = null;
      try {
        data = await response.json();
      } catch (err) {
        // non-JSON error page
      }
      if (response.ok && data && data.success) {
        saveLoyaltyCard({ word1, word2, stamps: data.stamps });
        setLinkWord1('');
        setLinkWord2('');
      } else if (response.status === 404) {
        setLoyaltyError('No card with those words — check the spelling, or join instead');
      } else if (response.status === 429) {
        setLoyaltyError('Too many attempts — please try again later');
      } else {
        setLoyaltyError((data && data.error) || 'Could not find your card — please try again');
      }
    } catch (error) {
      console.error('Error looking up loyalty card:', error);
      setLoyaltyError('Network error — please try again');
    }
    setLoyaltyBusy(false);
  };

  // Handle a loyalty error code from create-payment-intent / loyalty-free-order.
  // Returns true when the code was recognized and surfaced.
  const handleLoyaltyErrorCode = (result) => {
    if (!result || !result.code) return false;
    switch (result.code) {
      case 'loyalty_unknown':
      case 'not_found':
        clearLoyaltyCard();
        setLoyaltyCheckoutError('We couldn\'t find that loyalty card — it has been removed from this device.');
        return true;
      case 'wrong_word':
        setRedeemActive(false);
        setLoyaltyCheckoutError(
          result.attemptsRemaining > 0
            ? `Wrong word — ${result.attemptsRemaining} attempt${result.attemptsRemaining === 1 ? '' : 's'} remaining before a 15-minute lock`
            : 'Wrong word — card locked for 15 minutes'
        );
        return true;
      case 'locked':
        setRedeemActive(false);
        setLoyaltyCheckoutError('Too many wrong words — redemption is locked for 15 minutes. Stamps still count!');
        return true;
      case 'loyalty_insufficient':
      case 'insufficient':
        setRedeemActive(false);
        if (loyaltyCard && typeof result.stamps === 'number') {
          saveLoyaltyCard({ ...loyaltyCard, stamps: result.stamps });
        }
        setLoyaltyCheckoutError('Not enough stamps for a free drink yet');
        return true;
      case 'loyalty_no_drink':
        setRedeemActive(false);
        setLoyaltyCheckoutError('Add a drink to the basket to use your free drink');
        return true;
      case 'loyalty_invalid_word':
        setRedeemActive(false);
        setLoyaltyCheckoutError('Your private word must be a single word, letters only (accents welcome)');
        return true;
      case 'loyalty_use_free_order':
        // Total would be £0 / under Stripe's minimum — the free-drink-only
        // path (no payment) handles this basket.
        setLoyaltyCheckoutError('This order is fully covered by your free drink — use the Claim Free Drink button');
        return true;
      default:
        return false;
    }
  };

  // The loyalty portion of the create-payment-intent body
  const buildLoyaltyPayload = () => {
    if (!loyaltyCard || !includeLoyalty || fwofoMode) return {};
    const loyalty = { word1: loyaltyCard.word1, word2: loyaltyCard.word2 };
    if (redeemActive && redeemWord3) {
      loyalty.redeem = { word3: redeemWord3 };
    }
    return { loyalty };
  };

  // Free-drink-only basket: exactly one drink with the redeem toggled on —
  // skip Stripe entirely and use the no-payment endpoint.
  const isFreeDrinkOnlyOrder = redeemActive && basket.length === 1 && (basket[0].quantity || 1) === 1;

  // Optimistically bump the stamp count after a paid order with loyalty
  // attached (drinks earn one stamp each; a redeemed order also spends 9 and
  // the comped drink earns nothing), then fetch the true balance once the
  // webhook has had a moment to run.
  const bumpLoyaltyAfterPaidOrder = () => {
    const wasIncluded = includeLoyalty;
    const wasRedeeming = redeemActive;
    // Reset the per-order loyalty checkout state whatever happened
    setRedeemOpen(false);
    setRedeemActive(false);
    setRedeemWord3('');
    setIncludeLoyalty(true);
    setLoyaltyCheckoutError(null);
    if (!loyaltyCard || !wasIncluded || fwofoMode) return;
    const drinks = basket.reduce((sum, item) => sum + (item.quantity || 1), 0);
    const delta = wasRedeeming
      ? drinks - 1 - STAMPS_FOR_FREE_DRINK
      : drinks;
    saveLoyaltyCard({ ...loyaltyCard, stamps: Math.max(0, (loyaltyCard.stamps || 0) + delta) });
    const { word1, word2 } = loyaltyCard;
    setTimeout(() => {
      refreshLoyaltyCard(word1, word2);
    }, 5000);
  };

  const claimFreeDrink = async () => {
    setLoyaltyCheckoutError(null);
    if (!customerName) {
      setOrderResult({ success: false, error: 'Please enter your name' });
      return;
    }
    if (!loyaltyCard || !redeemWord3) {
      setLoyaltyCheckoutError('Enter your private word to claim your free drink');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/loyalty-free-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          word1: loyaltyCard.word1,
          word2: loyaltyCard.word2,
          word3: redeemWord3,
          item: { name: basket[0].name, variant: basket[0].variant },
          customerName,
          pickupTime
        })
      });
      let result = null;
      try {
        result = await response.json();
      } catch (err) {
        // non-JSON error page
      }
      if (response.ok && result && result.success) {
        if (typeof result.stamps === 'number') {
          saveLoyaltyCard({ ...loyaltyCard, stamps: result.stamps });
        }
        setOrderResult({
          success: true,
          message: 'Free drink claimed! We\'ll have your order ready shortly.'
        });
        setBasket([]);
        setCustomerName('');
        setCustomerEmail('');
        setClientSecret(null);
        setElements(null);
        setRedeemOpen(false);
        setRedeemActive(false);
        setRedeemWord3('');
        setIncludeLoyalty(true);
        if (result.apiOrderId) {
          offerPushNotifications(null, result.apiOrderId);
        }
      } else if (result && handleLoyaltyErrorCode(result)) {
        // handled inline
      } else {
        setOrderResult({
          success: false,
          error: (result && result.error) || 'Could not place your free order',
          message: result && result.reference ? `Reference: ${result.reference}` : undefined
        });
      }
    } catch (error) {
      console.error('Error claiming free drink:', error);
      setOrderResult({ success: false, error: 'Network error', message: error.message });
    }
    setLoading(false);
  };

  // Price of the currently selected syrup, from the server's product data
  // (same source the charge is calculated from). Fallback 60 if absent.
  const getSyrupPrice = () => {
    if (!selectedSyrup) return 0;
    const syrups = (selectedProduct && selectedProduct.addOns && selectedProduct.addOns.syrups) || [];
    const syrup = syrups.find(s => s.name === selectedSyrup);
    return syrup && typeof syrup.price === 'number' ? syrup.price : 60;
  };

  const buildCurrentItem = () => {
    const syrupPrice = getSyrupPrice();

    // Calculate base price (handle Hot Chocolate pricing)
    let basePrice = selectedProduct.price;
    if (selectedProduct.isChocolate && selectedProduct.priceInfo) {
      basePrice = size === 'Fluffy' ? selectedProduct.priceInfo.fluffy : selectedProduct.priceInfo.regular;
    }

    const itemPrice = basePrice + syrupPrice;

    // Determine VAT rate based on temperature
    // UK VAT: Hot drinks = 20% (standard rated), Iced drinks = 0% (zero-rated)
    const vatRate = temperature === 'Iced' ? 0 : 20;

    // Build variant string based on product type
    let variantParts = [temperature];

    if (selectedProduct.options.size.length > 0) {
      variantParts.push(size);
    }

    if (selectedProduct.options.milk.length > 0 && milk !== 'None') {
      variantParts.push(milk);
    }

    if (selectedProduct.options.caffeine && selectedProduct.options.caffeine.length > 1 && caffeine === 'Decaf') {
      variantParts.push('Decaf');
    }

    if (selectedSyrup) {
      variantParts.push(selectedSyrup + ' Syrup');
    }

    if (selectedTopping) {
      variantParts.push(selectedTopping + ' Topping');
    }

    if (selectedTeaFlavor) {
      variantParts.push(selectedTeaFlavor);
    }

    const variant = variantParts.join(', ');

    // Use base product name - flavor is in variant
    // Server-side catalog validates against base names only
    const productName = selectedProduct.name;

    return {
      id: Date.now(),
      name: productName,
      variant,
      price: itemPrice,
      vatRate: vatRate,
      quantity: 1
    };
  };

  const addToBasket = () => {
    const currentItem = buildCurrentItem();
    setBasket([...basket, currentItem]);

    // Reset customization
    setSelectedProduct(null);
    setTemperature('Hot');
    setMilk('None');
    setSize('Medium');
    setSelectedSyrup('');
    setSelectedTopping('');
    setSelectedTeaFlavor('');
  };

  const orderNow = () => {
    const currentItem = buildCurrentItem();
    // Add current item to basket and go to checkout
    setBasket([...basket, currentItem]);
    setShowBasket(true);
  };

  const removeFromBasket = (id) => {
    setBasket(basket.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    return basket.reduce((sum, item) => sum + item.price, 0);
  };

  // Invalidate payment intent when basket changes (items added/removed)
  // This ensures a new payment intent is created with the correct total
  useEffect(() => {
    if (clientSecret) {
      // Basket changed after payment intent was created - need to recreate
      setClientSecret(null);
      setElements(null);
      setPreloadedPickupTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket.length]); // Only watch basket length, not entire basket object

  // Invalidate payment intent when pickup time changes
  // This ensures a new payment intent is created with the correct pickup time
  useEffect(() => {
    if (clientSecret && preloadedPickupTime !== null && preloadedPickupTime !== pickupTime) {
      // Pickup time changed after payment intent was created - need to recreate
      console.log('Pickup time changed, invalidating payment intent');
      setClientSecret(null);
      setElements(null);
      setPreloadedPickupTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupTime]); // Watch for pickup time changes

  // Invalidate payment intent when customer name changes after pre-load
  // This ensures the payment intent has the complete name, not just what was typed when it pre-loaded
  useEffect(() => {
    if (clientSecret && customerName.length >= 2) {
      // Customer is still typing after payment intent was created - need to recreate
      console.log('Customer name changed after pre-load, invalidating payment intent');
      setClientSecret(null);
      setElements(null);
      setPreloadedPickupTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName]); // Watch for customer name changes

  // Invalidate payment intent when customer email changes after pre-load
  // This ensures the payment intent has the email for invoice creation
  useEffect(() => {
    if (clientSecret && customerEmail) {
      // Customer entered email after payment intent was created - need to recreate
      console.log('Customer email changed after pre-load, invalidating payment intent');
      setClientSecret(null);
      setElements(null);
      setPreloadedPickupTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerEmail]); // Watch for customer email changes

  // Invalidate payment intent when the loyalty attachment changes (chip
  // removed, free drink applied/unapplied) so the server re-prices the order
  useEffect(() => {
    if (clientSecret) {
      setClientSecret(null);
      setElements(null);
      setPreloadedPickupTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeLoyalty, redeemActive]);

  // Pre-load Payment Intent when user enters name (while they're typing)
  useEffect(() => {
    // Only pre-load if: name field touched OR has 2+ chars, has basket, in basket view, no client secret yet, not loading
    // Free-drink-only orders never touch Stripe (loyalty-free-order instead)
    if ((nameFieldTouched || customerName.length >= 2) && basket.length > 0 && showBasket && !clientSecret && !loading && !isFreeDrinkOnlyOrder) {
      // Debounce the API call - wait 500ms after user stops typing
      const timeoutId = setTimeout(async () => {
        try {
          const orderData = {
            items: basket.map(function(item) {
              return {
                name: item.name,
                quantity: item.quantity,
                variant: item.variant,
                price: item.price,
                vatRate: item.vatRate
              };
            }),
            customerName,
            customerEmail,
            pickupTime,
            ...buildLoyaltyPayload()
          };

          const response = await fetch('/.netlify/functions/create-payment-intent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderData)
          });

          // R7: fetch() doesn't throw on HTTP errors — parse the body
          // defensively and treat non-2xx as failures
          let result = null;
          try {
            result = await response.json();
          } catch (err) {
            console.error('Non-JSON response pre-loading payment:', response.status);
          }

          if (response.ok && result && result.success && result.clientSecret) {
            // Initialize Stripe with the publishable key from backend
            if (result.publishableKey && !window.Stripe) {
              // R5: Stripe.js never loaded (CDN blocked / ad blocker)
              console.error('Stripe.js is not available');
              setStripeUnavailable(true);
            } else {
              setStripeUnavailable(false);
            }
            if (!stripe && result.publishableKey && window.Stripe) {
              const stripeInstance = window.Stripe(result.publishableKey);
              setStripe(stripeInstance);
            }
            setClientSecret(result.clientSecret);
            setPreloadedPickupTime(pickupTime); // Track the pickup time used in this payment intent
          } else if (result && handleLoyaltyErrorCode(result)) {
            // loyalty problem surfaced inline; PI not created
          }
        } catch (error) {
          console.error('Error pre-loading payment:', error);
        }
      }, 500);

      return function() { clearTimeout(timeoutId); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName, customerEmail, basket, showBasket, clientSecret, loading, pickupTime, stripe, nameFieldTouched, includeLoyalty, redeemActive, isFreeDrinkOnlyOrder]);

  // Initialize Stripe Elements when we have a client secret
  useEffect(() => {
    if (stripe && clientSecret && !elements) {
      const options = {
        clientSecret,
        appearance: {
          theme: 'stripe',
        },
      };
      const elementsInstance = stripe.elements(options);
      setElements(elementsInstance);

      // Small delay to ensure DOM elements are ready
      setTimeout(() => {
        try {
          // Mount Express Checkout Element (Apple Pay, Google Pay, Link)
          const expressCheckoutElement = elementsInstance.create('expressCheckout', {
            paymentMethods: {
              applePay: 'auto',
              googlePay: 'auto',
              link: 'auto'
            }
          });

          // Handle Express Checkout button click (Apple Pay/Google Pay)
          expressCheckoutElement.on('confirm', async function(event) {
            console.log('Express Checkout confirm triggered');

            const result = await stripe.confirmPayment({
              elements: elementsInstance,
              confirmParams: {
                return_url: window.location.origin,
              },
              redirect: 'if_required'
            });

            if (result.error) {
              console.error('Payment error:', result.error);
              setOrderResult({
                success: false,
                error: result.error.message
              });
            } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
              console.log('Payment succeeded!');
              setOrderResult({
                success: true,
                message: 'Payment successful! We\'ll have your order ready shortly.'
              });
              bumpLoyaltyAfterPaidOrder();
              setBasket([]);
              setCustomerName('');
              setCustomerEmail('');
              setClientSecret(null);
              setElements(null);

              // Offer push notifications for order ready alerts
              offerPushNotifications(result.paymentIntent.id);
            }
          });

          expressCheckoutElement.mount('#express-checkout-element');

          expressCheckoutElement.on('ready', function() {
            console.log('Express Checkout Element ready');
          });

          // Mount Payment Element
          const paymentElement = elementsInstance.create('payment');
          paymentElement.mount('#payment-element');

          paymentElement.on('ready', function() {
            console.log('Payment Element ready');
          });
        } catch (error) {
          console.error('Error mounting Stripe elements:', error);
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, clientSecret, elements]);

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    if (!customerName) {
      setOrderResult({
        success: false,
        error: 'Please enter your name'
      });
      return;
    }

    // Email is optional, but if provided, validate it has @ symbol
    if (customerEmail && !customerEmail.includes('@')) {
      setOrderResult({
        success: false,
        error: 'Please enter a valid email address for your receipt'
      });
      return;
    }

    // If pickup time changed since pre-load, invalidate and recreate payment intent
    if (clientSecret && preloadedPickupTime !== pickupTime) {
      console.log('Pickup time changed from', preloadedPickupTime, 'to', pickupTime, '- recreating payment intent');
      setClientSecret(null);
      setElements(null);
      setPreloadedPickupTime(null);
      // Fall through to create new payment intent below
    } else if (clientSecret) {
      // Reuse pre-loaded payment intent (pickup time unchanged)
      return;
    }

    setLoading(true);
    setOrderResult(null);

    try {
      const orderData = {
        items: basket.map(function(item) {
          return {
            name: item.name,
            quantity: item.quantity,
            variant: item.variant,
            price: item.price,
            vatRate: item.vatRate
          };
        }),
        customerName,
        customerEmail,
        pickupTime,
        ...buildLoyaltyPayload()
      };

      // Create Payment Intent
      const response = await fetch('/.netlify/functions/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      // R7: check the HTTP status — fetch() doesn't throw on 4xx/5xx
      let result = null;
      try {
        result = await response.json();
      } catch (err) {
        console.error('Non-JSON response creating payment:', response.status);
      }

      if (response.ok && result && result.success && result.clientSecret) {
        // R5: detect Stripe.js failing to load
        if (result.publishableKey && !window.Stripe) {
          console.error('Stripe.js is not available');
          setStripeUnavailable(true);
        } else {
          setStripeUnavailable(false);
        }
        // Initialize Stripe with the publishable key from backend
        if (!stripe && result.publishableKey && window.Stripe) {
          const stripeInstance = window.Stripe(result.publishableKey);
          setStripe(stripeInstance);
        }
        setClientSecret(result.clientSecret);
        setPreloadedPickupTime(pickupTime); // Track the pickup time used in this payment intent
        setLoading(false);
      } else if (result && handleLoyaltyErrorCode(result)) {
        // Loyalty problem shown inline near the loyalty controls
        setLoading(false);
      } else {
        setOrderResult({
          success: false,
          error: 'Failed to initialize payment',
          message: (result && (result.message || result.error)) || 'Please try again'
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      setOrderResult({
        success: false,
        error: 'Network error',
        message: error.message
      });
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // R5: never fail silently — tell the customer what's wrong
      setStripeUnavailable(true);
      setOrderResult({
        success: false,
        error: 'Payment temporarily unavailable',
        message: 'The payment system could not load. Please reload the page or try again shortly.'
      });
      return;
    }

    setPaymentProcessing(true);

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin,
      },
    });

    if (result.error) {
      setOrderResult({
        success: false,
        error: result.error.message
      });
      setPaymentProcessing(false);
    }
  };

  const handleAcceptCookies = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setShowCookieBanner(false);
  };

  // Check if running on iOS Safari (not as PWA)
  const isIOSSafariNotPWA = () => {
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
    const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
    return isIOS && isSafari && !isStandalone;
  };

  // Check if we should offer push notifications.
  // Paid orders pass a paymentIntentId (order id looked up via
  // get-order-by-payment); loyalty free orders pass the barista order id
  // directly as knownOrderId.
  const offerPushNotifications = (paymentIntentId, knownOrderId) => {
    setNotificationSetupFailed(false);

    // Paid orders can carry an Apple Wallet order ticket — its status flips
    // to "Ready" on the lock screen with nothing to install, which is the
    // only ready-alert iOS Safari (non-PWA) customers can get.
    setOrderTicketPI(paymentIntentId && paymentIntentId.startsWith('pi_') ? paymentIntentId : null);

    // On iOS Safari (not PWA), show hint to add to home screen instead
    if (isIOSSafariNotPWA()) {
      console.log('iOS Safari detected (not PWA) - showing home screen hint');
      setShowIOSHint(true);
      return;
    }

    // Check if push notifications are supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      console.log('Push notifications not supported in this browser');
      return;
    }

    // Check if already granted or denied
    if (Notification.permission === 'granted') {
      // Already have permission, just subscribe
      setupPushNotifications(paymentIntentId, knownOrderId);
      return;
    }

    if (Notification.permission === 'denied') {
      console.log('Notifications previously denied');
      return;
    }

    // Show prompt to user
    setPendingPaymentIntentId(paymentIntentId || (knownOrderId ? `order:${knownOrderId}` : null));
    setShowNotificationPrompt(true);
  };

  // Called when user clicks "Enable Notifications" button (user gesture required)
  const enableNotifications = async () => {
    setShowNotificationPrompt(false);

    if (!pendingPaymentIntentId) return;

    try {
      const permission = await Notification.requestPermission();
      console.log('Permission result:', permission);

      if (permission === 'granted') {
        await setupPushNotifications(pendingPaymentIntentId);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setNotificationSetupFailed(true);
    }

    setPendingPaymentIntentId(null);
  };

  // Setup push notifications (called after permission granted)
  const setupPushNotifications = async (paymentIntentId, knownOrderId) => {
    // Loyalty free orders have no payment intent — the order id is passed
    // directly (or encoded as 'order:<id>' via the prompt flow)
    if (!knownOrderId && paymentIntentId && paymentIntentId.startsWith('order:')) {
      knownOrderId = paymentIntentId.slice('order:'.length);
      paymentIntentId = null;
    }
    console.log('Setting up push notifications for payment:', paymentIntentId, 'order:', knownOrderId);
    setNotificationSetupFailed(false);

    try {
      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      console.log('Service worker ready');

      // Get VAPID public key
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        console.log('VAPID public key not configured');
        return;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      console.log('Push subscription created');

      // Get order ID to link the subscription
      let orderId = knownOrderId || null;
      let retries = 0;
      const maxRetries = 5;

      while (!orderId && paymentIntentId && retries < maxRetries) {
        try {
          const response = await fetch(`/.netlify/functions/get-order-by-payment?payment_intent=${paymentIntentId}`);
          // R7: only trust the body on a 2xx response
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.orderId) {
              orderId = data.orderId;
              break;
            }
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
          retries++;
        } catch (err) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          retries++;
        }
      }

      if (!orderId) {
        console.log('Could not get order ID');
        setNotificationSetupFailed(true);
        return;
      }

      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      const registerResponse = await fetch('/.netlify/functions/register-push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, subscription: subscriptionJson })
      });
      if (!registerResponse.ok) {
        console.error('Push registration failed:', registerResponse.status);
        setNotificationSetupFailed(true);
        return;
      }

      console.log('Push notification registered for order:', orderId);
    } catch (error) {
      console.error('Error setting up push notifications:', error);
      setNotificationSetupFailed(true);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content clickable" onClick={() => {
          setSelectedProduct(null);
          setShowBasket(false);
          setShowLoyalty(false);
          setOrderResult(null);
        }}>
          <img src="/logo.svg" alt="Middle Street Coffee" className="logo" width="100" height="100" />
          <h1>Middle Street Coffee</h1>
        </div>
        <p className="test-mode">Order Online - Collect in Shop</p>
        {!fwofoMode && (
          <button
            className="loyalty-link"
            onClick={() => {
              setSelectedProduct(null);
              setShowBasket(false);
              setOrderResult(null);
              setLoyaltyError(null);
              setShowLoyalty(true);
            }}
          >
            Loyalty{loyaltyCard && (loyaltyCard.stamps || 0) >= STAMPS_FOR_FREE_DRINK ? ' — free drink ready' : ''}
          </button>
        )}
        {fwofoMode && (
          <button
            className="exit-fwofo-btn"
            onClick={() => {
              setFwofoMode(false);
              setBasket([]);
              setShowBasket(false);
              window.history.replaceState({}, '', '/');
            }}
          >
            Exit FWOFO Mode
          </button>
        )}
        {!fwofoMode && basket.length > 0 && (
          <button className="basket-btn" onClick={() => setShowBasket(!showBasket)}>
            Basket ({basket.length})
          </button>
        )}
      </header>

      <main className="container">
        {orderResult && (
          <div className={`result ${orderResult.success ? 'success' : 'error'}`}>
            {orderResult.success ? (
              <div>
                <h2>Order Submitted</h2>
                <p>We'll have your order ready for collection shortly</p>
                {IS_APPLE_DEVICE && orderTicketPI && (
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                    <p style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                      Add your order to Apple Wallet — it flips to
                      &ldquo;Ready&rdquo; on your lock screen when it's waiting.
                    </p>
                    <button
                      className="wallet-add-btn"
                      onClick={() => submitOrderTicketForm(orderTicketPI)}
                    >
                      <AddToWalletBadge />
                    </button>
                  </div>
                )}
                {showNotificationPrompt && (
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
                    <p style={{ marginBottom: '10px' }}>Get notified when your order is ready?</p>
                    <button
                      onClick={enableNotifications}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#007AFF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        cursor: 'pointer',
                        marginRight: '10px'
                      }}
                    >
                      Enable Notifications
                    </button>
                    <button
                      onClick={() => setShowNotificationPrompt(false)}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#ccc',
                        color: '#333',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        cursor: 'pointer'
                      }}
                    >
                      No Thanks
                    </button>
                  </div>
                )}
                {showIOSHint && (
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px', textAlign: 'left' }}>
                    <p style={{ marginBottom: '8px', fontWeight: '500' }}>Want notifications when your order is ready?</p>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                      Tap the <span style={{ fontWeight: '500' }}>Share</span> button below, then <span style={{ fontWeight: '500' }}>"Add to Home Screen"</span> to enable notifications for future orders.
                    </p>
                    <button
                      onClick={() => setShowIOSHint(false)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'transparent',
                        color: '#666',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Got it
                    </button>
                  </div>
                )}
                {notificationSetupFailed && (
                  <p style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
                    Notifications couldn't be enabled — we'll still have your order ready.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <h2>Error</h2>
                <p>{orderResult.error}</p>
                {orderResult.message && <p>Details: {orderResult.message}</p>}
              </div>
            )}
          </div>
        )}

        {showLoyalty ? (
          <div className="loyalty-view">
            <button className="back-btn" onClick={() => {
              setShowLoyalty(false);
              setLoyaltyError(null);
              setJustJoined(null);
            }}>
              Back to Menu
            </button>

            {justJoined ? (
              <div className="loyalty-joined">
                <h2>Welcome!</h2>
                <p>You are</p>
                <p className="loyalty-words-big">{justJoined.word1} {justJoined.word2}</p>
                {(justJoined.stamps || 0) > 0 && (
                  <p className="free-ready">
                    Your {justJoined.stamps} stamp{justJoined.stamps === 1 ? '' : 's'} from the shop
                    {justJoined.stamps === 1 ? ' is' : ' are'} already on it.
                  </p>
                )}
                <p className="loyalty-explain">
                  That's your card — no app or QR code needed, just say it at the till.
                  Keep your private word to yourself; you'll only need it to claim your free drink.
                </p>
                {IS_APPLE_DEVICE && justJoined.word3 && (
                  <div className="wallet-add">
                    <p className="loyalty-hint">
                      Pop it straight into Apple Wallet — it keeps its own stamp count
                      up to date and appears when you're near the shop.
                    </p>
                    <button
                      className="wallet-add-btn"
                      onClick={() => submitWalletPassForm(justJoined.word1, justJoined.word2, justJoined.word3, false)}
                    >
                      <AddToWalletBadge />
                    </button>
                  </div>
                )}
                <button
                  className="submit-btn"
                  onClick={() => setJustJoined(null)}
                >
                  Show My Card
                </button>
              </div>
            ) : loyaltyCard ? (
              <div className="loyalty-card-panel">
                <h2>Your Loyalty Card</h2>
                {claimInfo && claimInfo.status === 'open' && (
                  <p className="loyalty-hint">
                    Receipt codes create a brand-new card, so code {claimInfo.code} can't be
                    added to this one — next time say your two words at the till and the
                    stamps go straight on your card.
                  </p>
                )}
                <p className="loyalty-words-big">{loyaltyCard.word1} {loyaltyCard.word2}</p>
                <div className="stamp-row" aria-label={`${Math.min(loyaltyCard.stamps || 0, STAMPS_FOR_FREE_DRINK)} of ${STAMPS_FOR_FREE_DRINK} stamps`}>
                  {Array.from({ length: 10 }).map((_, i) => {
                    const stamps = loyaltyCard.stamps || 0;
                    if (i === 9) {
                      return (
                        <span key={i} className={`stamp free-dot${stamps >= STAMPS_FOR_FREE_DRINK ? ' ready' : ''}`}>
                          ★
                        </span>
                      );
                    }
                    return <span key={i} className={`stamp${i < Math.min(stamps, STAMPS_FOR_FREE_DRINK) ? ' filled' : ''}`}></span>;
                  })}
                </div>
                {(loyaltyCard.stamps || 0) >= STAMPS_FOR_FREE_DRINK ? (
                  <p className="free-ready">Free drink ready! Use it at checkout here, or say your words at the till.</p>
                ) : (
                  <p className="loyalty-progress">
                    {STAMPS_FOR_FREE_DRINK - (loyaltyCard.stamps || 0)} more drink{STAMPS_FOR_FREE_DRINK - (loyaltyCard.stamps || 0) === 1 ? '' : 's'} until your free one
                  </p>
                )}
                <p className="loyalty-explain">
                  Buy 9 drinks, get the 10th free. Just say your two words at the till — no app, no QR code.
                </p>
                {IS_APPLE_DEVICE && (
                  <div className="wallet-add">
                    {walletOpen ? (
                      <form onSubmit={handleWalletAdd} className="wallet-form">
                        <p className="loyalty-hint">
                          Pop your card into Apple Wallet — it keeps its own stamp count
                          up to date and reminds you when you're near the shop.
                          Enter your private word to prove it's yours.
                        </p>
                        <div className="form-group">
                          <label>Your private word</label>
                          <input
                            type="text"
                            value={walletWord3}
                            onChange={function(e) { setWalletWord3(e.target.value); }}
                            autoCapitalize="none"
                            autoCorrect="off"
                            autoComplete="off"
                          />
                        </div>
                        <label className="wallet-print-choice">
                          <input
                            type="checkbox"
                            checked={walletPrintSecret}
                            onChange={function(e) { setWalletPrintSecret(e.target.checked); }}
                          />
                          <span>
                            Print my private word on the back of the pass, in case I forget it
                            (anyone you hand your phone to could flip the pass over and read it)
                          </span>
                        </label>
                        {walletError && <p className="loyalty-error">{walletError}</p>}
                        <button type="submit" className="wallet-add-btn">
                          <AddToWalletBadge />
                        </button>
                        <button
                          type="button"
                          className="loyalty-clear-btn"
                          onClick={function() { setWalletOpen(false); setWalletWord3(''); setWalletError(null); }}
                        >
                          Not now
                        </button>
                      </form>
                    ) : (
                      <button className="wallet-add-btn" onClick={function() { setWalletOpen(true); }}>
                        <AddToWalletBadge />
                      </button>
                    )}
                  </div>
                )}
                <button className="loyalty-clear-btn" onClick={clearLoyaltyCard}>
                  This isn't my card
                </button>
              </div>
            ) : (
              <div className="loyalty-start">
                <h2>Loyalty</h2>
                <p className="loyalty-explain">
                  Buy 9 drinks and your 10th is free — your card is just two words you say at the till, no app or QR code needed.
                </p>

                {claimInfo && claimInfo.status === 'open' && (
                  <p className="free-ready">
                    Thanks for your visit! You've got {claimInfo.stamps} stamp{claimInfo.stamps === 1 ? '' : 's'} waiting
                    — create your card below and {claimInfo.stamps === 1 ? "it's" : "they're"} yours.
                  </p>
                )}
                {claimInfo && claimInfo.status === 'claimed' && (
                  <p className="loyalty-error">That receipt code has already been claimed.</p>
                )}
                {claimInfo && claimInfo.status === 'expired' && (
                  <p className="loyalty-error">That receipt code has expired — codes last 30 days.</p>
                )}
                {claimInfo && claimInfo.status === 'void' && (
                  <p className="loyalty-error">That receipt code is no longer valid — the sale it came from was refunded.</p>
                )}
                {claimInfo && (claimInfo.status === 'not_found' || claimInfo.status === 'error') && (
                  <p className="loyalty-error">That receipt code wasn't recognised — check the receipt and try the link again.</p>
                )}

                {loyaltyError && <p className="loyalty-error">{loyaltyError}</p>}

                <div className="loyalty-option">
                  <h3>{claimInfo && claimInfo.status === 'open' ? 'Claim your stamps' : 'Join'}</h3>
                  <p className="loyalty-hint">
                    Pick the two words your card goes by — your name, a nickname,
                    anything you'll remember — plus a private word only you know
                    (you'll say it when you claim a free drink).
                  </p>
                  <form onSubmit={handleLoyaltyJoin}>
                    {!pickForMe && (
                      <div className="loyalty-word-inputs">
                        <div className="form-group">
                          <label>First public word</label>
                          <input
                            type="text"
                            value={joinPublic1}
                            onChange={function(e) { setJoinPublic1(e.target.value); }}
                            placeholder="e.g. dave"
                            autoCapitalize="none"
                            autoCorrect="off"
                          />
                        </div>
                        <div className="form-group">
                          <label>Second public word</label>
                          <input
                            type="text"
                            value={joinPublic2}
                            onChange={function(e) { setJoinPublic2(e.target.value); }}
                            placeholder="e.g. latte"
                            autoCapitalize="none"
                            autoCorrect="off"
                          />
                        </div>
                      </div>
                    )}
                    <label className="loyalty-pickforme">
                      <input
                        type="checkbox"
                        checked={pickForMe}
                        onChange={function(e) { setPickForMe(e.target.checked); }}
                      />
                      {' '}Pick my two words for me
                    </label>
                    <div className="form-group">
                      <label>Your private word</label>
                      <input
                        type="text"
                        value={joinWord}
                        onChange={function(e) { setJoinWord(e.target.value); }}
                        placeholder="e.g. marmalade"
                        autoCapitalize="none"
                        autoCorrect="off"
                      />
                    </div>
                    <div className="form-group">
                      <label>Type it again</label>
                      <input
                        type="text"
                        value={joinWordConfirm}
                        onChange={function(e) { setJoinWordConfirm(e.target.value); }}
                        placeholder="same word"
                        autoCapitalize="none"
                        autoCorrect="off"
                      />
                    </div>
                    <button type="submit" className="submit-btn" disabled={loyaltyBusy}>
                      {loyaltyBusy ? 'Creating...'
                        : (claimInfo && claimInfo.status === 'open' ? 'Claim My Stamps' : 'Get My Card')}
                    </button>
                  </form>
                </div>

                <div className="loyalty-option">
                  <h3>I have a card</h3>
                  <p className="loyalty-hint">Enter your two public words.</p>
                  <form onSubmit={handleLoyaltyLink}>
                    <div className="loyalty-word-inputs">
                      <div className="form-group">
                        <label>First word</label>
                        <input
                          type="text"
                          value={linkWord1}
                          onChange={function(e) { setLinkWord1(e.target.value); }}
                          placeholder="e.g. snowy"
                          autoCapitalize="none"
                          autoCorrect="off"
                        />
                      </div>
                      <div className="form-group">
                        <label>Second word</label>
                        <input
                          type="text"
                          value={linkWord2}
                          onChange={function(e) { setLinkWord2(e.target.value); }}
                          placeholder="e.g. lantern"
                          autoCapitalize="none"
                          autoCorrect="off"
                        />
                      </div>
                    </div>
                    <button type="submit" className="submit-btn" disabled={loyaltyBusy}>
                      {loyaltyBusy ? 'Looking up...' : 'Find My Card'}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : showBasket ? (
          <div className="basket-view">
            {!fwofoMode && (
              <button className="back-btn" onClick={() => setShowBasket(false)}>
                Back to Menu
              </button>
            )}

            {fwofoMode ? (
              <div className="fwofo-basket-header">
                <h1>FLAT WHITE OR FUCK OFF MODE</h1>
                <p>Hot Flat White • £3.60 • No modifications, no removals</p>
              </div>
            ) : (
              <h2>Your Basket</h2>
            )}

            {basket.length === 0 ? (
              <p>Your basket is empty</p>
            ) : (
              <>
                <div className="basket-items">
                  {basket.map(item => (
                    <div key={item.id} className="basket-item">
                      <div>
                        <h3>{fwofoMode ? 'Hot Flat White' : item.name}</h3>
                        {!fwofoMode && <p className="variant">{item.variant}</p>}
                        <p className="price">£{(item.price / 100).toFixed(2)}</p>
                      </div>
                      {!fwofoMode && (
                        <button
                          className="remove-btn"
                          onClick={() => removeFromBasket(item.id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="basket-total">
                  {redeemActive && basket.length > 0 ? (
                    <strong>
                      Total: £{(Math.max(0, calculateTotal() - Math.max(...basket.map(item => item.price))) / 100).toFixed(2)}
                      <span className="loyalty-discount-note"> (free drink applied)</span>
                    </strong>
                  ) : (
                    <strong>Total: £{(calculateTotal() / 100).toFixed(2)}</strong>
                  )}
                </div>

                {!fwofoMode && loyaltyCard && includeLoyalty && (
                  <div className="loyalty-chip">
                    <span>Collecting stamps as <strong>{loyaltyCard.word1} {loyaltyCard.word2}</strong></span>
                    <button
                      type="button"
                      className="loyalty-chip-remove"
                      aria-label="Don't collect stamps on this order"
                      onClick={() => {
                        setIncludeLoyalty(false);
                        setRedeemOpen(false);
                        setRedeemActive(false);
                        setRedeemWord3('');
                        setLoyaltyCheckoutError(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {!fwofoMode && loyaltyCard && includeLoyalty &&
                  (loyaltyCard.stamps || 0) >= STAMPS_FOR_FREE_DRINK && basket.length > 0 && (
                  <div className="loyalty-redeem-box">
                    {!redeemOpen && !redeemActive ? (
                      <button
                        type="button"
                        className="loyalty-redeem-toggle"
                        onClick={() => {
                          setRedeemOpen(true);
                          setLoyaltyCheckoutError(null);
                        }}
                      >
                        Use your free drink (−£{(Math.max(...basket.map(item => item.price)) / 100).toFixed(2)})
                      </button>
                    ) : redeemActive ? (
                      <div className="loyalty-redeem-applied">
                        <span>Free drink applied</span>
                        <button
                          type="button"
                          className="loyalty-chip-remove"
                          aria-label="Remove free drink"
                          onClick={() => {
                            setRedeemActive(false);
                            setRedeemOpen(false);
                            setRedeemWord3('');
                            setLoyaltyCheckoutError(null);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="loyalty-redeem-form">
                        <div className="form-group">
                          <label>Your private word</label>
                          <input
                            type="password"
                            value={redeemWord3}
                            onChange={function(e) { setRedeemWord3(e.target.value); }}
                            placeholder="the word only you know"
                            autoCapitalize="none"
                            autoCorrect="off"
                          />
                        </div>
                        <div className="loyalty-redeem-actions">
                          <button
                            type="button"
                            className="loyalty-redeem-apply"
                            disabled={!redeemWord3.trim()}
                            onClick={() => {
                              setLoyaltyCheckoutError(null);
                              setRedeemActive(true);
                            }}
                          >
                            Apply Free Drink
                          </button>
                          <button
                            type="button"
                            className="loyalty-redeem-cancel"
                            onClick={() => {
                              setRedeemOpen(false);
                              setRedeemWord3('');
                              setLoyaltyCheckoutError(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {loyaltyCheckoutError && (
                  <p className="loyalty-error">{loyaltyCheckoutError}</p>
                )}

                <form onSubmit={handleSubmitOrder}>
                  <div className="form-group">
                    <label>Your Name</label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={function(e) { setCustomerName(e.target.value); }}
                      onFocus={function() { setNameFieldTouched(true); }}
                      required
                      placeholder="Enter your name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email (optional - for receipt)</label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={function(e) { setCustomerEmail(e.target.value); }}
                      placeholder="your@email.com (optional)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Pickup Time</label>
                    <select value={pickupTime} onChange={function(e) { setPickupTime(e.target.value); }}>
                      {pickupTimes.length > 0 ? (
                        pickupTimes.map(function(time) {
                          return (
                            <option key={time.value} value={time.value}>
                              {time.label}
                            </option>
                          );
                        })
                      ) : (
                        <option value="now">Now</option>
                      )}
                    </select>
                  </div>
                </form>

                {isFreeDrinkOnlyOrder ? (
                  <div className="payment-section">
                    <button
                      type="button"
                      className="submit-btn"
                      disabled={loading || !customerName}
                      onClick={claimFreeDrink}
                    >
                      {loading ? 'Placing Order...' : 'Claim Free Drink — £0.00'}
                    </button>
                    {!customerName && <p className="loyalty-hint">Enter your name above first</p>}
                  </div>
                ) : stripeUnavailable ? (
                  <div className="payment-unavailable">
                    <p>
                      Payment temporarily unavailable — the payment system couldn't load
                      (this can be caused by an ad blocker or a network problem).
                    </p>
                    <button onClick={() => window.location.reload()}>Reload Page</button>
                  </div>
                ) : clientSecret ? (
                  <div className="payment-section">
                    <div id="express-checkout-element"></div>

                    <div className="payment-divider">
                      <span>Or pay with card</span>
                    </div>

                    <form onSubmit={handlePayment}>
                      <div id="payment-element"></div>

                      <button
                        type="submit"
                        className="submit-btn"
                        disabled={paymentProcessing || !stripe || !elements}
                      >
                        {paymentProcessing ? 'Processing...' : 'Pay Now'}
                      </button>
                    </form>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : !selectedProduct ? (
          <div className="menu">
            <h2>Select a Drink</h2>
            {!shopOpen && (
              <div className="result error">
                <h2>Shop Closed</h2>
                <p>{closedMessage || "Sun–Thu 06:30–16:00 | Fri–Sat 06:30–21:00"}</p>
                {!closedMessage && <p>Place your order from 06:00</p>}
              </div>
            )}
            {products.length === 0 && menuError ? (
              <div className="menu-error">
                <p>Unable to load the menu — please check your connection.</p>
                <button
                  className="retry-btn"
                  onClick={() => {
                    setMenuError(false);
                    fetchProducts();
                    fetchPickupTimes();
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : products.length === 0 ? (
              <p>Loading menu...</p>
            ) : (
              <div className="product-grid">
                {products.map((product, index) => (
                  <div
                    key={index}
                    className="product-card"
                    onClick={() => {
                      if (shopOpen) {
                        // Reset all customization options when selecting a new product
                        setTemperature(product.options.temperature.length === 1 ? product.options.temperature[0] : 'Hot');
                        setSize(product.options.size.length > 0 ? product.options.size[0] : 'Medium');
                        setMilk('None');
                        setCaffeine('Regular');
                        setSelectedSyrup('');
                        setSelectedTopping('');
                        setSelectedTeaFlavor('');
                        setSelectedProduct(product);
                      }
                    }}
                    style={shopOpen ? {} : { opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    <h3>{product.name}</h3>
                    <p className="price">£{(product.price / 100).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="customize">
            <button className="back-btn" onClick={() => setSelectedProduct(null)}>
              Back to Menu
            </button>

            <h2>{selectedProduct.name}</h2>

            <div className="customization-form">
              {selectedProduct.options.temperature.length > 1 && (
                <div className="form-group">
                  <label>Temperature</label>
                  <select value={temperature} onChange={(e) => setTemperature(e.target.value)}>
                    {selectedProduct.options.temperature.map(temp => (
                      <option key={temp}>{temp}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedProduct.options.size.length > 0 && (
                <div className="form-group">
                  <label>Size</label>
                  <select value={size} onChange={(e) => setSize(e.target.value)}>
                    {selectedProduct.options.size.map(s => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedProduct.options.milk.length > 0 && (
                <div className="form-group">
                  <label>Milk</label>
                  <select value={milk} onChange={(e) => setMilk(e.target.value)}>
                    {selectedProduct.options.milk.map(m => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedProduct.options.caffeine && selectedProduct.options.caffeine.length > 1 && (
                <div className="form-group">
                  <label>Caffeine</label>
                  <select value={caffeine} onChange={(e) => setCaffeine(e.target.value)}>
                    {selectedProduct.options.caffeine.map(c => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedProduct.addOns && selectedProduct.addOns.syrups && selectedProduct.addOns.syrups.length > 0 && (
                <div className="form-group">
                  <label>Syrup (Optional)</label>
                  <select value={selectedSyrup} onChange={(e) => setSelectedSyrup(e.target.value)}>
                    <option value="">No Syrup</option>
                    {selectedProduct.addOns.syrups.map(syrup => (
                      <option key={syrup.name} value={syrup.name}>
                        {syrup.name} (+£{(syrup.price / 100).toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedProduct.addOns && selectedProduct.addOns.toppings && selectedProduct.addOns.toppings.length > 0 && (
                <div className="form-group">
                  <label>Topping (Optional)</label>
                  <select value={selectedTopping} onChange={function(e) { setSelectedTopping(e.target.value); }}>
                    <option value="">No Topping</option>
                    {selectedProduct.addOns.toppings.map(function(topping) {
                      return (
                        <option key={topping.name} value={topping.name}>
                          {topping.name}{topping.price > 0 ? ' (+£' + (topping.price / 100).toFixed(2) + ')' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {selectedProduct.addOns && selectedProduct.addOns.teaFlavors && selectedProduct.addOns.teaFlavors.length > 0 && (
                <div className="form-group">
                  <label>Tea Flavor</label>
                  <select
                    value={selectedTeaFlavor}
                    onChange={(e) => setSelectedTeaFlavor(e.target.value)}
                    required
                  >
                    <option value="">Select a tea...</option>
                    {selectedProduct.addOns.teaFlavors.map(tea => (
                      <option key={tea.name} value={tea.name}>
                        {tea.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="item-total">
                <strong>
                  £{(() => {
                    let price = selectedProduct.price;
                    if (selectedProduct.isChocolate && selectedProduct.priceInfo) {
                      price = size === 'Fluffy' ? selectedProduct.priceInfo.fluffy : selectedProduct.priceInfo.regular;
                    }
                    return ((price + getSyrupPrice()) / 100).toFixed(2);
                  })()}
                </strong>
              </div>

              <div className="action-buttons">
                <button
                  type="button"
                  className="order-now-btn"
                  onClick={orderNow}
                  disabled={selectedProduct.isSpecialtyTea && !selectedTeaFlavor}
                >
                  {basket.length > 0 ? 'Order This + Basket Items' : 'Order Now'}
                </button>
                <button
                  type="button"
                  className="add-to-basket-btn"
                  onClick={addToBasket}
                  disabled={selectedProduct.isSpecialtyTea && !selectedTeaFlavor}
                >
                  Add to Basket
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {showCookieBanner && (
        <div className="cookie-banner">
          <p>This site uses essential cookies for secure payments via Stripe. <a href="/about.html" target="_blank">Learn more about cookies and payments</a></p>
          <button onClick={handleAcceptCookies}>OK</button>
        </div>
      )}

      <footer className="app-footer">
        <div className="shop-info">
          <h3>Pickup Location</h3>
          <p>10 Broad Street, Deal, CT14 6ER</p>
          <p>Sun–Thu 06:30–16:00 | Fri–Sat 06:30–21:00</p>
          <p>
            <a href="https://api.whatsapp.com/message/LNBSWB7KTQVBG1" target="_blank" rel="noopener noreferrer" className="whatsapp-link">
              WhatsApp Us
            </a>
          </p>
        </div>
        <div className="footer-links">
          <p>
            <button
              className="footer-loyalty-link"
              onClick={() => {
                setSelectedProduct(null);
                setShowBasket(false);
                setOrderResult(null);
                setLoyaltyError(null);
                setShowLoyalty(true);
                window.scrollTo(0, 0);
              }}
            >
              Loyalty
            </button>
            {' · '}
            <a href="/about.html" target="_blank" rel="noopener noreferrer">About</a>
            {' · '}
            <a href="https://middlest.coffee" target="_blank" rel="noopener noreferrer">middlest.coffee</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

// R4: wrap the whole app in an error boundary so a render crash shows a
// friendly reload screen instead of a blank page
export default function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
